import React, { type FC } from 'react';

import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { functionWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { fg } from '@atlassian/jira-feature-gating';
import Placeholder from '@atlassian/jira-placeholder';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { lazyAfterPaint } from '@atlassian/react-loosely-lazy';
import { PACKAGE_NAME, TEAM_NAME } from '../../common/constants.tsx';
import { useIsEligibleForTrialAssistantReminder } from '../../services/eligibility-check/index.tsx';
import type ReminderNudgeType from './index.tsx';

type Props = {
	targetId: string;
};

const AsyncLazyReminderNudge = lazyAfterPaint<typeof ReminderNudgeType>(() =>
	import(/* webpackChunkName: "async-jira-trial-assistant-reminder-nudge" */ './index.tsx').then(
		(module) => module.ReminderNudge,
	),
);

const JiraTrialAssistantReminderNudgeAsync: FC<Props> = ({ targetId }) => {
	const isEligible =
		useIsEligibleForTrialAssistantReminder() &&
		expValEquals('custom_rovo_onboarding_agent', 'cohort', 'variation', 'not-enrolled');

	const triggerRovo = () => {
		// TODO: LUNA-648 Configure OOTB Rovo Trigger
	};

	if (!isEligible) {
		return <></>;
	}

	return (
		<JSErrorBoundary
			id="JiraTrialAssistantReminderNudge"
			packageName={PACKAGE_NAME}
			teamName={TEAM_NAME}
			fallback="unmount"
		>
			<UFOSegment name="jira-trial-assistant-reminder-nudge">
				<Placeholder name="jira-trial-assistant-reminder-nudge" fallback={null}>
					<AsyncLazyReminderNudge targetId={targetId} onPrimaryCta={triggerRovo} />
				</Placeholder>
			</UFOSegment>
		</JSErrorBoundary>
	);
};

export const JiraTrialAssistantReminderNudge = functionWithCondition(
	() => fg('custom_rovo_onboarding_agent_gate'),
	JiraTrialAssistantReminderNudgeAsync,
	() => <></>,
);
